import {ALLMA_JOB_TYPE_HOGGER} from '~/constants/allmaJobs'
import {
    QUESTION_CATEGORIES,
    QUESTION_OPTION_CONFIG_NUMBER,
    QUESTION_OPTION_CONFIG_TEXT,
    QUESTION_OPTION_CONFIG_YESNO,
    type PhaseConfig,
} from '~/constants/questions'
import type {FullContract} from '~/models/contract'
import type {
    GMServicesCommonQuestionsQuestionDefinition,
    GMServicesCommonQuestionsQuestionResponse,
    GMServicesCommonQuestionsQuestionSet,
    GMServicesCommonQuestionsQuestionTemplate,
} from '~/gen/openapi/sblService'

// Not always a suffix: retentiontreesaccountedforamount -> retentiontreesamount
const ACCOUNTED_FOR_MARKER = 'accountedfor'

export const getQuestionPairKey = (logicalName?: string | null): string | undefined => {
    if (!logicalName) {
        return undefined
    }
    return logicalName.replace(ACCOUNTED_FOR_MARKER, '')
}

const RETENTION_TREES_AMOUNT = 'retentiontreesamount'

// PEFC: one retention tree per daa of felled area, thinning excluded
const sumHoggerActionsArea = (contract?: FullContract): number =>
    (contract?.AllmaJobs || [])
        .filter((job) => !job.Removed && job.JobType === ALLMA_JOB_TYPE_HOGGER)
        .flatMap((job) => job.Actions || [])
        .reduce((sum, action) => sum + (action?.Area || 0), 0)

export const getDefaultQuestionValue = (
    definition?: GMServicesCommonQuestionsQuestionDefinition,
    contract?: FullContract,
): string | undefined => {
    if (definition?.LogicalName !== RETENTION_TREES_AMOUNT) {
        return undefined
    }
    const area = Math.round(sumHoggerActionsArea(contract))
    return area ? String(area) : undefined
}

export const OPTION_YES = 'True'
export const OPTION_NO = 'False'
export const OPTION_NOT_RELEVANT = 'NotRelevant'

// A quantity may legitimately be left blank; an assessment may not
// A definition without an answer type is information only, like the Forarbeid points
export const hasAnswerType = (definition: GMServicesCommonQuestionsQuestionDefinition): boolean =>
    [QUESTION_OPTION_CONFIG_TEXT, QUESTION_OPTION_CONFIG_YESNO, QUESTION_OPTION_CONFIG_NUMBER]
        .includes(definition.OptionConfig as unknown as number)

export const isOptionalQuantity = (definition: GMServicesCommonQuestionsQuestionDefinition): boolean =>
    definition.OptionConfig as unknown as number === QUESTION_OPTION_CONFIG_NUMBER && !definition.Required

// Stored answers are not consistently cased, so compare and render through this
export const normalizeYesNoValue = <T>(value: T): T | string => {
    if (typeof value !== 'string') {
        return value
    }
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') {
        return OPTION_YES
    }
    if (normalized === 'false') {
        return OPTION_NO
    }
    if (normalized === 'notrelevant') {
        return OPTION_NOT_RELEVANT
    }
    return value
}

// Gives the set one response per template question, keeps existing answers and drops
// responses the template no longer has. Safe to run repeatedly.
export const instantiateQuestionSet = (
    contract: FullContract,
    contractKey: PhaseConfig['contractKey'],
    template?: GMServicesCommonQuestionsQuestionTemplate,
    definitions?: Array<GMServicesCommonQuestionsQuestionDefinition>,
): void => {
    if (!contract[contractKey]) {
        contract[contractKey] = {QuestionResponses: []}
    }
    const set = contract[contractKey]!
    if (!set.QuestionResponses) {
        set.QuestionResponses = []
    }
    const responses = set.QuestionResponses

    const created = (template?.QuestionDefinitions || [])
        .filter((def) => !responses.some((qr) => qr.QuestionDefinitionId === def.Id))
        .map((def) => ({QuestionDefinitionId: def.Id, Value: '', Comment: ''}))
    responses.push(...created)

    // Only responses created in this run, so a value the user cleared is never refilled
    created.forEach((response) => {
        const definition = definitions?.find((d) => d.Id === response.QuestionDefinitionId)
        const defaultValue = getDefaultQuestionValue(definition, contract)
        if (defaultValue !== undefined) {
            response.Value = defaultValue
        }
    })

    // No template means there is nothing to clean up against
    if (!template?.QuestionDefinitions?.length) {
        return
    }
    set.QuestionResponses = responses.filter(
        (r) => template.QuestionDefinitions!.some((def) => def.Id === r.QuestionDefinitionId),
    )
}

// Ascending Order; definitions without one go last
export const compareQuestionOrder = (a: GMServicesCommonQuestionsQuestionDefinition, b: GMServicesCommonQuestionsQuestionDefinition): number => {
    if (!a.Order && !b.Order) return 0
    if (!a.Order) return 1
    if (!b.Order) return -1
    return a.Order - b.Order
}

export interface EnvironmentQuestionRow {
    definition: GMServicesCommonQuestionsQuestionDefinition
    before?: GMServicesCommonQuestionsQuestionResponse
    after?: GMServicesCommonQuestionsQuestionResponse
}

export interface EnvironmentQuestionGroup {
    heading: string
    rows: EnvironmentQuestionRow[]
}

// The before set decides which questions exist; the after answer is paired in through the logical name
export const groupEnvironmentQuestions = (
    definitions: Array<GMServicesCommonQuestionsQuestionDefinition>,
    beforeSet?: GMServicesCommonQuestionsQuestionSet | null,
    afterSet?: GMServicesCommonQuestionsQuestionSet | null,
): EnvironmentQuestionGroup[] => {
    const byId = new Map(definitions.map((d) => [d.Id, d]))
    const afterByPairKey = new Map<string, GMServicesCommonQuestionsQuestionResponse>()
    afterSet?.QuestionResponses?.forEach((response) => {
        const pairKey = getQuestionPairKey(byId.get(response.QuestionDefinitionId!)?.LogicalName)
        if (pairKey) {
            afterByPairKey.set(pairKey, response)
        }
    })

    const rows: EnvironmentQuestionRow[] = (beforeSet?.QuestionResponses || [])
        .map((response) => ({definition: byId.get(response.QuestionDefinitionId!), before: response}))
        .filter((row): row is {definition: GMServicesCommonQuestionsQuestionDefinition, before: GMServicesCommonQuestionsQuestionResponse} => !!row.definition)
        .sort((a, b) => compareQuestionOrder(a.definition, b.definition))
        .map((row) => ({...row, after: afterByPairKey.get(getQuestionPairKey(row.definition.LogicalName) ?? '')}))

    if (!rows.some((row) => !!row.definition.Category)) {
        return rows.length ? [{heading: 'DUE_DILIGENCE_ASSESSMENT', rows}] : []
    }

    const categoryOf = (row: EnvironmentQuestionRow) => row.definition.Category as unknown as number
    const groups: EnvironmentQuestionGroup[] = QUESTION_CATEGORIES
        .map((category) => ({heading: `QUESTION_CATEGORY_${category}`, rows: rows.filter((row) => categoryOf(row) === category)}))
        .filter((group) => group.rows.length)
    const uncategorised = rows.filter((row) => !row.definition.Category)
    if (uncategorised.length) {
        groups.push({heading: 'QUESTION_CATEGORY_MISSING', rows: uncategorised})
    }
    return groups
}
